<template>
  <div
    v-if="isRelevant"
    class="-transcript-layer"
    :style="{
      '--margin-top': `${$props.marginTop}px`,
      '--arrow-right': `${$props.arrowRight}px`,
    }"
  >
    <aside class="-arrow" />

    <wt-tabs
      v-if="showTabs"
      type="horizontal bottom"
      :tabs="$data.uiTabs"
      :current-tab-id="currentTabId"
      @tab-selected="$_onTabSelected"
    />

    <template v-if="tabs.providerTab.selected">
      <div class="-title wt2-text-l2" v-text="title" />
      <div
        class="-content"
        :data-tab="tabs.providerTab.id"
        v-text="editorialTranscript"
      />
    </template>

    <template v-if="tabs.voTab.selected">
      <div v-if="!showTabs" class="-tab-label" v-text="tabs.voTab.label" />
      <div class="-content -tokens" :data-tab="tabs.voTab.id">
        <span
          v-for="(token, key) in audioTranscript"
          :key="key"
          :class="{ '-in-range': token.inRange }"
        >
          <br v-if="token.newLine" />
          <template v-else>
            {{ token.content }}
          </template>
        </span>
      </div>
    </template>

    <template v-if="tabs.transcribedTab.selected">
      <div
        v-if="!showTabs"
        class="-tab-label"
        v-text="tabs.transcribedTab.label"
      />
      <div class="-content -tokens" :data-tab="tabs.transcribedTab.id">
        <span
          v-for="(token, key) in videoTranscript"
          :key="key"
          :class="{ '-in-range': token.inRange }"
        >
          {{ token.content }}
        </span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { isTokenRelevantInSpan } from 'v/Story/Timeline/api/transcription';

export default {
  name: 'TranscriptLayer',

  props: {
    toggle: { type: Boolean, required: false, default: false },
    marginTop: { type: Number, required: true },
    arrowRight: { type: Number, required: true },
  },

  data() {
    const tabs = {
      voTab: { label: 'Voice Over', selected: false },
      providerTab: { label: 'From Provider', selected: false },
      transcribedTab: { label: 'Transcript', selected: false },
    };
    _.each(tabs, (value, key) => {
      value.id = key;
      value.classes = { backgroundColor: 'wt2-bgcolor-primary' };
    });

    return {
      tabs,
      uiTabs: [],
    };
  },

  computed: {
    ...mapState({
      transcriptions: (state) => state.story.transcriptions,
    }),
    ...mapState('assetEditor', ['workModel', 'narrationAssets']),
    ...mapGetters('assetEditor', ['assetTranscription']),
    ...mapState('timemachine', {
      tmTrackLayerAudio: (state) => _.map(state.trackLayer.audio, 'data'),
    }),

    isRelevant() {
      return this.toggle;
    },

    showTabs() {
      let count = 0;

      count += !_.isEmpty(this.tmTrackLayerAudio) ? 1 : 0;
      count += !_.isEmpty(this.editorialTranscript) ? 1 : 0;
      count += this.assetTranscription ? 1 : 0;

      return count > 1;
    },

    currentTabId() {
      return _.find(this.$data.tabs, { selected: true }).id;
    },

    videoTranscript() {
      const rv = [];
      const range = {
        startTime: this.workModel.startTime,
        endTime: this.workModel.endTime,
      };

      _.each(this.assetTranscription?.tokens, (token) => {
        rv.push({
          inRange: isTokenRelevantInSpan(token, range),
          content: token.content,
        });
      });

      if (rv.length === 0) {
        rv.push({ content: 'Transcript not available' });
      }

      return rv;
    },

    audioTranscript() {
      const rv = [];
      const range = {
        startTime: this.workModel.displayStartTime,
        endTime: this.workModel.displayEndTime,
      };

      let prevAudioIndex = 0;
      _.each(this.tmTrackLayerAudio, (tmAudio, audioIndex) => {
        const audioTranscript = this.transcriptions[tmAudio.metadata.assetUUID];

        _.each(audioTranscript?.tokens, (token) => {
          const isRelevantForAudio = isTokenRelevantInSpan(token, {
            startTime: tmAudio.relativeStartTime,
            endTime: tmAudio.relativeEndTime,
          });

          if (isRelevantForAudio) {
            const changingTracks =
              prevAudioIndex !== audioIndex && rv.length > 0;

            if (changingTracks) {
              prevAudioIndex = audioIndex;
              rv.push({ newLine: true });
            }

            // arranging token times relative to workModel (for simplicity)
            const tokenStartTime =
              tmAudio.displayStartTime -
              tmAudio.relativeStartTime +
              token.startTime;
            const tokenEndTime =
              tmAudio.displayStartTime -
              tmAudio.relativeStartTime +
              token.endTime;
            const isRelevantForEditedAsset = isTokenRelevantInSpan(
              {
                startTime: tokenStartTime,
                endTime: tokenEndTime,
              },
              range
            );

            rv.push({
              inRange: isRelevantForEditedAsset,
              content: token.content,
            });
          }
        });
      });

      if (rv.length === 0) {
        rv.push({ content: 'Transcript not available' });
      }

      return rv;
    },

    title() {
      return this.workModel.metadata.title;
    },

    editorialTranscript() {
      return this.workModel.metadata.transcript;
    },
  },

  created() {
    if (!_.isEmpty(this.tmTrackLayerAudio)) {
      this.uiTabs.push(this.tabs.voTab);
    }

    if (this.assetTranscription) {
      this.uiTabs.push(this.tabs.transcribedTab);
    }

    if (!_.isEmpty(this.editorialTranscript)) {
      this.uiTabs.push(this.tabs.providerTab);
    }

    if (!_.isEmpty(this.uiTabs)) {
      this.$_selectTab(_.first(this.uiTabs).id);
    }
  },

  methods: {
    $_selectTab(tabId) {
      _.each(this.$data.tabs, (tab, id) => {
        tab.selected = id === tabId;
      });
    },

    $_onTabSelected(tab) {
      this.$_selectTab(tab.id);
    },
  },
};
</script>

<style lang="scss" scoped>
.-transcript-layer {
  position: absolute;
  z-index: 99;
  top: var(--margin-top);
  background: white;
  word-break: break-word;
  width: 100%;
  height: calc(100% - var(--margin-top));
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;

  > .-arrow {
    position: absolute;
    top: -9px;
    right: var(--arrow-right);
    border: 9px solid white;
    transform: rotate(45deg);
  }

  > .wt2-tabs {
    margin: 30px 30px 0 30px;
    width: 420px;
    height: 34px;
    border-bottom: 1px solid #ccc;
    align-self: center;

    /deep/ .wt2-tabs-menu .wt2-tabs-menu-tab {
      width: 50%;
      justify-content: center;
      font-size: $font-size-n;
    }
  }

  > .-title {
    margin: 30px;
    user-select: text;
  }

  > .-tab-label {
    margin: 30px 0 0px 30px;
    font-size: 14px;
    font-weight: 500;
  }

  > .-content {
    padding: 0 30px;
    overflow-x: hidden;
    overflow-y: auto;
    height: inherit;
    font-size: 14px;
    line-height: 24px;
    flex-grow: 1;

    &.-tokens {
      padding-top: 30px;
      color: rgba(0, 0, 0, 0.3);
      .-in-range {
        color: $color-normal;
      }
    }
  }
}
</style>
